import Image from "next/image"
import { cn } from "cn"

import { useMapWeather } from "@/lib/hooks/use-map-weather"
import { Locale } from "@/lib/locale-hooks"
import { weatherIconSrc } from "@/lib/weather-utils"
import { useMapState } from "./map-state-context"

export interface MapWeatherBadgeProps {
  /** Pauses fetching and hides the badge when false. */
  enabled: boolean
  className?: string
}

/** Map-overlay weather pill. Reads bounds/zoom from MapStateContext. */
export function MapWeatherBadge({ enabled, className }: MapWeatherBadgeProps) {
  const { mapBounds, mapZoom } = useMapState()
  const unitSystem = Locale.useTemperatureUnitSystem()
  const formatWeatherTemp = Locale.useFormatWeatherTemp()
  const { currentWeather, shouldShowWeatherForZoom } = useMapWeather({
    bbox: mapBounds,
    zoom: mapZoom ?? 0,
    enabled,
  })

  if (!enabled || !shouldShowWeatherForZoom || !currentWeather) return null

  const { icon, description, temp } = currentWeather
  const weatherIcon = weatherIconSrc(icon)
  const weatherTemp = formatWeatherTemp(temp, unitSystem)

  if (!weatherTemp && !weatherIcon && !description) return null

  return (
    <div
      className={cn(
        "absolute bottom-21 left-2.5 flex items-center rounded-full bg-background/90 py-1 pr-2.5 pl-2 text-2xs text-foreground shadow-(--button-shadow) backdrop-blur-xs sm:bottom-8 sm:left-5",
        className
      )}
    >
      {weatherIcon && (
        <Image
          width={18}
          height={18}
          src={weatherIcon}
          alt={description || "weather icon"}
          unoptimized
          className="shrink-0"
        />
      )}
      {weatherTemp && <span className="no-translate mr-1">{weatherTemp}</span>}
      {description && (
        <span className="text-muted first-letter:capitalize">
          {description}
        </span>
      )}
    </div>
  )
}
